/**
 * "Have an invite?" — the code a friend sent, typed or pasted (GH-864e). Used on `/waitlist` and in
 * the profile sheet. The code is normalized the way the server does (`normalizeInviteCode`: case,
 * spaces, hyphens and Crockford look-alikes), and a string that cannot be a code is refused here
 * rather than spent against the server's rate limit.
 */
import { useState } from "react";
import { inviteCodeSchema, MEMBERSHIP_COPY } from "@givehere/contracts";
import { DonorApiError } from "./donor-session.js";

export const ALREADY_MEMBER = "You’re already a member.";

/** One sentence for a failed redeem — uniform for every way an invite can be unavailable. */
export function redeemFailure(caught: unknown): string {
  if (caught instanceof DonorApiError) {
    if (caught.code === "invite_unavailable" || caught.status === 404) {
      return MEMBERSHIP_COPY.inviteUnavailable;
    }
    if (caught.code === "already_member") return ALREADY_MEMBER;
    return caught.message;
  }
  return "That didn’t work. Try again.";
}

export function InviteCodeForm({
  busy,
  onSubmit,
  initial = "",
}: {
  busy: boolean;
  onSubmit: (code: string) => void;
  /** GH-871b: a code already typed on the sign-in sheet, carried into the retry. */
  initial?: string;
}) {
  const [raw, setRaw] = useState(initial);
  const [invalid, setInvalid] = useState(false);
  return (
    <form
      className="invite-form"
      onSubmit={(submitted) => {
        submitted.preventDefault();
        const parsed = inviteCodeSchema.safeParse(raw);
        setInvalid(!parsed.success);
        if (parsed.success) onSubmit(parsed.data);
      }}
    >
      <label className="field">
        <span>{MEMBERSHIP_COPY.enterInviteCode}</span>
        <input
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={32}
          value={raw}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? "invite-form-error" : undefined}
          onChange={(changed) => setRaw(changed.target.value)}
        />
      </label>
      {invalid && (
        <p id="invite-form-error" className="notice notice--error" role="alert">
          That isn’t an invite code. Check it and try again.
        </p>
      )}
      <button type="submit" className="pay" disabled={busy || raw.trim().length === 0}>
        {busy ? "Joining…" : "Join"}
      </button>
    </form>
  );
}
